import { useState, useEffect } from "react";
import api, { getApiErrorMessage } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import SaleStatusBadge from "../../components/SaleStatusBadge";
import {
  POINT_TRANSACTION_KIND,
  formatLevel,
  formatPoints,
  getPointTransactionKind,
} from "../../lib/display";
import { Coins, TrendingUp, Gift, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import PublicationFeed from "../../components/PublicationFeed";
import { useNavigate } from "react-router-dom";

const defaultPointsConfig = {
  niveis: {
    Bronze: 0,
    Prata: 2000,
    Ouro: 5000,
  },
};

const transactionStyles = {
  [POINT_TRANSACTION_KIND.EARNED]: {
    bg: "bg-green-100",
    icon: "text-green-600",
    points: "text-green-600",
    Icon: Coins,
  },
  [POINT_TRANSACTION_KIND.SPENT]: {
    bg: "bg-red-100",
    icon: "text-red-600",
    points: "text-red-600",
    Icon: Coins,
  },
  [POINT_TRANSACTION_KIND.SALE_REVERSAL]: {
    bg: "bg-amber-100",
    icon: "text-amber-700",
    points: "text-amber-700",
    Icon: RotateCcw,
  },
};

const getSaleCode = (venda) => (
  venda?.numero_documento || venda?.id_venda_erp || venda?.id?.slice(0, 8) || "Venda"
);

const getTransactionTitle = (transacao, venda) => {
  if (getPointTransactionKind(transacao) !== POINT_TRANSACTION_KIND.SALE_REVERSAL || !venda) {
    return transacao.descricao;
  }

  const status = String(venda.status_venda || "").toLowerCase();
  if (status.includes("cancel")) return `Cancelamento da venda #${getSaleCode(venda)}`;
  if (status.includes("parcial")) return `Devolução parcial da venda #${getSaleCode(venda)}`;
  return `Devolução da venda #${getSaleCode(venda)}`;
};

const formatSignedPoints = (value) => {
  const numberValue = Number(value || 0);
  return `${numberValue >= 0 ? "+" : "-"}${formatPoints(Math.abs(numberValue))}`;
};

const PartnerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [parceiro, setParceiro] = useState(null);
  const [transacoes, setTransacoes] = useState([]);
  const [vendasMap, setVendasMap] = useState({});
  const [pointsConfig, setPointsConfig] = useState(defaultPointsConfig);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      if (user?.parceiro_id) {
        const [parceiroRes, transacoesRes, configRes, vendasRes] = await Promise.all([
          api.get(`/parceiros/${user.parceiro_id}`),
          api.get(`/transacoes?parceiro_id=${user.parceiro_id}`),
          api.get("/points/config").catch(() => ({ data: defaultPointsConfig })),
          api.get("/vendas"),
        ]);
        setParceiro(parceiroRes.data);
        setTransacoes(transacoesRes.data);
        setVendasMap(Object.fromEntries(vendasRes.data.map((venda) => [venda.id, venda])));
        setPointsConfig(configRes.data || defaultPointsConfig);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(getApiErrorMessage(error, "Erro ao carregar seu painel."));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-32 bg-slate-200 rounded-2xl animate-pulse" />
        ))}
      </div>
    );
  }

  const thresholds = {
    ...defaultPointsConfig.niveis,
    ...(pointsConfig?.niveis || {}),
    ...(parceiro?.niveis_lojas?.find((item) => item.fixa)?.niveis || {}),
  };
  const saldoPontos = Number(parceiro?.pontos_nivel || 0);
  const bronzeMin = Number(thresholds.Bronze || 0);
  const prataMin = Number(thresholds.Prata || 2000);
  const ouroMin = Number(thresholds.Ouro || 5000);

  const nextLevel = parceiro?.nivel === "Bronze" ? "Prata" : parceiro?.nivel === "Prata" ? "Ouro" : null;
  const currentLevelMin = parceiro?.nivel === "Prata" ? prataMin : bronzeMin;
  const nextLevelMin = nextLevel === "Prata" ? prataMin : nextLevel === "Ouro" ? ouroMin : currentLevelMin;
  const levelProgress = parceiro
    ? parceiro.nivel === "Ouro" || nextLevelMin <= currentLevelMin
      ? 100
      : Math.min(Math.max(((saldoPontos - currentLevelMin) / (nextLevelMin - currentLevelMin)) * 100, 0), 100)
    : 0;
  const pointsToNext = nextLevel ? Math.max(nextLevelMin - saldoPontos, 0) : 0;
  const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
  const totalGanhoMes = transacoes.filter((t) => new Date(t.created_at) >= monthStart).reduce((sum, t) => {
    const kind = getPointTransactionKind(t);
    if (kind === POINT_TRANSACTION_KIND.EARNED) return sum + Number(t.pontos || 0);
    if (kind === POINT_TRANSACTION_KIND.SALE_REVERSAL) return sum - Number(t.pontos || 0);
    return sum;
  }, 0);
  const totalResgates = transacoes.filter(t => getPointTransactionKind(t) === POINT_TRANSACTION_KIND.SPENT).length;

  return (
    <div className="min-w-0 space-y-6 animate-fade-in" data-testid="partner-dashboard">
      {/* Points Card */}
      <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-indigo-600 p-5 text-white sm:rounded-3xl sm:p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-blue-100 text-sm">Seu saldo</p>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className="break-all font-heading text-3xl font-bold sm:text-4xl">
                {formatPoints(parceiro?.saldo_pontos)}
              </span>
              <span className="text-blue-200">pontos</span>
            </div>
          </div>
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/20">
            <Coins className="h-6 w-6" />
          </div>
        </div>
        
        {/* Level Progress */}
        <p className="text-sm text-blue-100">{parceiro?.loja_nome || "Loja fixa"} · {formatPoints(parceiro?.pontos_nivel)} pontos acumulados em vendas</p>
        <div className="mt-4">
          <div className="mb-2 flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:justify-between">
            <span className={`px-3 py-1 rounded-full font-medium ${
              parceiro?.nivel === "Ouro" 
                ? "bg-yellow-400/20 text-yellow-200" 
                : parceiro?.nivel === "Prata" 
                  ? "bg-slate-300/20 text-slate-200" 
                  : "bg-orange-400/20 text-orange-200"
            }`}>
              {formatLevel(parceiro?.nivel)}
            </span>
            {nextLevel && (
              <span className="text-blue-200 sm:text-right">
                {pointsToNext > 0 ? `${formatPoints(pointsToNext)} pts para ${nextLevel}` : `Você é ${formatLevel(parceiro?.nivel)}!`}
              </span>
            )}
          </div>
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: `${levelProgress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      {parceiro?.niveis_lojas?.length > 1 && <div className="grid gap-3 sm:grid-cols-2">{parceiro.niveis_lojas.filter((item) => !item.fixa).map((item) => <div key={item.loja_id} className="rounded-lg border bg-white p-4"><p className="font-medium">{item.loja_nome}</p><p className="text-sm text-slate-600">{item.nivel} · {formatPoints(item.pontos)} pontos em vendas</p></div>)}</div>}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <button type="button" onClick={() => navigate("/parceiro/historico")} className="bg-white rounded-lg p-4 text-left shadow-sm transition-transform hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-primary">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-green-100">
              <TrendingUp className="h-5 w-5 text-green-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-slate-500">Este mês</p>
              <p className="font-heading font-bold text-lg text-slate-900">
                {formatSignedPoints(totalGanhoMes)}
              </p>
            </div>
          </div>
        </button>
        <button type="button" onClick={() => navigate("/parceiro/resgates")} className="bg-white rounded-lg p-4 text-left shadow-sm transition-transform hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-primary">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-purple-100">
              <Gift className="h-5 w-5 text-purple-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-slate-500">Resgatados</p>
              <p className="font-heading font-bold text-lg text-slate-900">
                {totalResgates}
              </p>
            </div>
          </div>
        </button>
      </div>

      <PublicationFeed />

      {/* Recent Transactions */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h3 className="font-heading font-semibold text-slate-900">
            Últimas transações
          </h3>
        </div>
        <div className="divide-y divide-slate-100">
          {transacoes.length > 0 ? (
            transacoes.slice(0, 5).map((transacao) => {
              const transactionKind = getPointTransactionKind(transacao);
              const style = transactionStyles[transactionKind] || transactionStyles[POINT_TRANSACTION_KIND.EARNED];
              const TransactionIcon = style.Icon;
              const pointsValue = Number(transacao.pontos || 0);
              const signedPoints = transactionKind === POINT_TRANSACTION_KIND.EARNED ? pointsValue : -pointsValue;
              const venda = transacao.venda_id ? vendasMap[transacao.venda_id] : null;
              const isSaleReversal = transactionKind === POINT_TRANSACTION_KIND.SALE_REVERSAL;

              return (
                <div key={transacao.id} className="flex items-start justify-between gap-3 p-4">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${style.bg}`}>
                      <TransactionIcon className={`h-5 w-5 ${style.icon}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="break-words text-sm font-medium text-slate-900">
                        {getTransactionTitle(transacao, venda)}
                      </p>
                      {isSaleReversal && venda && (
                        <div className="mt-1">
                          <SaleStatusBadge status={venda.status_venda} />
                        </div>
                      )}
                      {isSaleReversal && (
                        <p className="mt-1 text-xs text-amber-700">
                          Pontos estornados desta venda
                        </p>
                      )}
                      <p className="text-xs text-slate-500">
                        {new Date(transacao.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                  <span className={`shrink-0 font-heading font-bold ${style.points}`}>
                    {formatSignedPoints(signedPoints)}
                  </span>
                </div>
              );
            })
          ) : (
            <div className="p-8 text-center text-slate-500">
              Nenhuma transação ainda
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerDashboard;
